import { Component, Inject, OnInit, Optional, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RegistrationStage, SmsRegistrationService } from '@galaxy/sms';
import { BusinessSelectorDialogComponent } from '@vendasta/businesses';
import {
  ConversationChannel,
  GlobalParticipantType,
  PlatformLocation,
  SubjectParticipant,
} from '@vendasta/conversation';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  Observable,
  combineLatest,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
  firstValueFrom,
} from 'rxjs';
import { ConversationGoogleService } from '../../../../core/src/lib/channels/conversation-google.service';
import { ConversationWebchatService } from '../../../../core/src/lib/channels/conversation-webchat.service';
import { InboxNavigationService } from '../../../../core/src/lib/inbox-navigation.service';
import { InboxService } from '../../../../core/src/lib/inbox.service';
import { ConversationConfig } from '../../../../core/src/lib/interface/config.interface';
import { ParticipantService } from '../../../../core/src/lib/participant.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_CONFIG_TOKEN,
  CONVERSATION_GOOGLE_BUSINESS_MESSAGES_AVAILABLE_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_IMAGE_SERVICE_TOKEN,
  CONVERSATION_ROUTES_TOKEN,
  CONVERSATION_SMS_ENABLED_TOKEN,
  InboxImageService,
  PARTNER_ID_TOKEN,
  GROUP_ID_TOKEN,
} from '../../../../core/src/lib/tokens';
import { InboxApiService } from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Component({
  selector: 'inbox-welcome',
  templateUrl: './inbox-welcome.component.html',
  styleUrls: ['./inbox-welcome.component.scss'],
})
export class InboxWelcomeComponent implements OnInit {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly inboxNav = inject(InboxNavigationService);
  private readonly participantService = inject(ParticipantService);

  isBusinessApp = this.inboxService.isBusinessApp;
  private readonly smsRegistrationService = this.isBusinessApp ? inject(SmsRegistrationService) : null;

  constructor(
    private readonly analyticsService: ProductAnalyticsService,
    private router: Router,
    private inboxService: InboxService,
    private dialog: MatDialog,
    private inboxApiService: InboxApiService,
    @Inject(CONVERSATION_IMAGE_SERVICE_TOKEN) readonly inboxImageService: InboxImageService,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) readonly accountGroupId$: Observable<string>,
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    @Optional() @Inject(CONVERSATION_CONFIG_TOKEN) private readonly inboxConfig: ConversationConfig,
    @Inject(CONVERSATION_GOOGLE_BUSINESS_MESSAGES_AVAILABLE_TOKEN)
    readonly googleBusinessMessagesAvailable$: Observable<boolean | undefined | null>,
    private readonly conversationWebchatService: ConversationWebchatService,
    private readonly conversationGoogleService: ConversationGoogleService,
    @Inject(CONVERSATION_SMS_ENABLED_TOKEN) private smsEnabled$: Observable<boolean>,
    @Inject(GROUP_ID_TOKEN) readonly groupId$: Observable<string>,
    private snackbarService: SnackbarService,
  ) {}

  optInClicked = false;

  isPartnerChatEnabled$ = this.inboxService.allowSmbChatWithPartner$;

  displayA2pCard$ = this.inboxService.displayA2pCard$();
  multiLocationEnabled$ = this.inboxService.multiLocationEnabled$;
  displayA2pLink$ =
    this.smsRegistrationService !== null
      ? combineLatest([
          this.smsRegistrationService.getAccountGroupInfo$().pipe(map((info) => info.napData.country)),
          this.displayA2pCard$,
          this.smsRegistrationService.registrationStage$,
        ]).pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
          map(([country, displayA2pCard, registrationStage]) => {
            return country === 'US' && displayA2pCard && registrationStage !== RegistrationStage.RegistrationComplete;
          }),
        )
      : of(false);

  displayGoogleLink$ = combineLatest([
    this.googleBusinessMessagesAvailable$,
    this.conversationGoogleService.isGoogleBusinessMessagesEnabled$,
  ]).pipe(
    map(([googleBusinessMessagesAvailable, isGoogleBusinessMessagesEnabled]) => {
      return googleBusinessMessagesAvailable && !isGoogleBusinessMessagesEnabled;
    }),
  );

  displayWebchatLink$ = this.inboxService.canAccessWebChatSettings$.pipe(
    switchMap((canAccessWebchat) => {
      if (canAccessWebchat) {
        return this.conversationWebchatService.isAnyWebchatEnabled$.pipe(
          map((isAnyWebchatEnabled) => canAccessWebchat && !isAnyWebchatEnabled),
        );
      }
      return of(false);
    }),
  );

  isMultiLocation = this.hostAppInterface.getAppOptions().is_multilocation;
  showNewMessage = !this.inboxConfig?.hideNewMessageButton;
  platformLocation = this.inboxService.platformLocation;
  partnerBrandName$ = this.inboxConfig?.partnerBrandName$;

  isOnlyPartnerChatAvailable$ = combineLatest([
    this.isPartnerChatEnabled$,
    this.displayA2pLink$,
    this.displayGoogleLink$,
    this.displayWebchatLink$,
    this.smsEnabled$,
  ]).pipe(
    map(([allowPartnerChat, displayA2pLink, displayGoogleLink, displayWebchatLink, smsEnabled]) => {
      return !!(
        this.isBusinessApp &&
        allowPartnerChat &&
        !displayA2pLink &&
        !displayGoogleLink &&
        !displayWebchatLink &&
        !smsEnabled
      );
    }),
  );

  settingsUrl$ = this.accountGroupId$.pipe(
    filter((accountGroupId) => !!accountGroupId),
    map((accountGroupId) => `/account/location/${accountGroupId}/settings/inbox`),
  );

  private readonly routes = toSignal(inject(CONVERSATION_ROUTES_TOKEN));

  ngOnInit(): void {
    this.analyticsService.trackEvent('inbox', 'home', 'view');
  }

  newMessage(): void {
    switch (this.platformLocation) {
      case PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
        this.messageFromBusinessCenter();
        break;
      case PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
      case PlatformLocation.PLATFORM_LOCATION_SALES_CENTER:
        this.messageFromPartnerCenter();
        break;
      case PlatformLocation.PLATFORM_LOCATION_VENDOR_CENTER:
        this.messageFromVendorCenter();
        break;
      default:
        console.warn(`New message not supported in ${this.platformLocation}.`);
        break;
    }
  }

  messageFromBusinessCenter(): void {
    const routes = this.routes();
    if (!routes) return;

    combineLatest([
      this.partnerId$,
      this.accountGroupId$,
      this.inboxService.canAccessSMS$,
      this.participantService.buildIAMUserParticipant(),
    ])
      .pipe(take(1))
      .subscribe(([partnerId, accountGroupId, canAccessSMS, currentIAMParticipant]) => {
        if (canAccessSMS === true) {
          this.router.navigate([routes.sendNewMessage]);
        } else {
          this.hostAppInterface.redirectToInternalConversation(
            currentIAMParticipant,
            [
              new SubjectParticipant({
                internalParticipantId: accountGroupId,
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
              }),
              new SubjectParticipant({
                internalParticipantId: partnerId,
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
              }),
            ],
            ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
          );
        }
      });
  }

  messageFromPartnerCenter(): void {
    const currentIAMParticipant = this.participantService.buildIAMUserParticipant();
    this.partnerId$
      .pipe(
        switchMap((partnerId) => {
          return this.dialog
            .open(BusinessSelectorDialogComponent, {
              width: '500px',
              data: {
                partnerId: partnerId,
              },
            })
            .afterClosed();
        }),
        withLatestFrom(this.partnerId$, currentIAMParticipant),
        take(1),
      )
      .subscribe(([business, partnerId, currentParticipant]) => {
        if (business) {
          this.inboxNav.gotoConversation(
            [
              new SubjectParticipant({
                internalParticipantId: partnerId,
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
              }),
              new SubjectParticipant({
                internalParticipantId: business?.accountGroupId,
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
              }),
            ],
            currentParticipant,
            ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
          );
        }
      });
  }

  messageFromVendorCenter(): void {
    const routes = this.routes();
    if (!routes) return;
    this.router.navigate([routes.root, 'resellers']);
  }

  async optIn(): Promise<void> {
    const groupId = await firstValueFrom(this.groupId$);
    try {
      await firstValueFrom(
        this.inboxApiService.upsertProductFeature({
          productFeature: {
            multiLocationEnabled: true,
          },
          fieldMask: {
            paths: ['multi_location_enabled'],
          },
          subjectParticipant: {
            participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_GROUP,
            internalParticipantId: groupId,
          },
        }),
      );
      this.snackbarService.openSuccessSnack('INBOX.MULTI_LOCATION.SUCCESS_TO_OPT_IN', { duration: Infinity });
      this.optInClicked = true;
    } catch {
      this.snackbarService.openErrorSnack('INBOX.MULTI_LOCATION.FAIL_TO_OPT_IN');
    }
  }
}
